import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProviderService } from '../../provider.service';
import { DischemApiService } from '../../dischem-api.service';
import { MedicationPrescription } from '../../api-client.service';
import {
  DISCHEM_ORDER_SUFFIX,
  DISCHEM_STATUS_LABELS,
  DISCHEM_DELIVERED_CODES,
  DISCHEM_CANCELLABLE_CODES,
  DischemOrderStatusCode,
  DischemOrderStatusRecord,
  buildDischemTimelineSteps,
} from '../../models/dischem.model';
import {
  PharmacyEscriptDescriptor,
  EscriptStatusRecord,
  EscriptSendContext,
  EscriptSendResult,
  EscriptCancelContext,
  EscriptTimelineStep,
  UnifiedStatusView,
} from '../pharmacy-escript.descriptor';
import { UnifiedPharmacy, dischemToUnified } from '../unified-pharmacy.model';
import { FirestoreStreamStatusSource } from '../sources/firestore-stream-status.source';

/** Dis-Chem vendor descriptor — webhook stream status (with pre-append 1 / infer-cancelled 3), cancel, HTML doc view. */
@Injectable({ providedIn: 'root' })
export class DischemDescriptor implements PharmacyEscriptDescriptor {
  readonly id = 'dischem';
  readonly displayName = 'Dis-Chem';
  readonly chip = { kind: 'image' as const, value: '/assets/images/dischem.png' };
  readonly sentField = 'SentToDischem';
  readonly identifierField = 'OrderNumber';
  readonly orderNumberSuffix = DISCHEM_ORDER_SUFFIX;
  // Dis-Chem dispenses the e-script as the original script (Defect 49699, product decision 2026-09-07)
  readonly transferAsOriginalScript = true;
  readonly statusMode = 'stream' as const;
  readonly ingest = {
    kind: 'csv' as const,
    columns: {
      siteNo: 'Site no.',
      practiceNo: 'Practice Nr',
      yNumbers: 'Y Numbers',
      storeName: 'Store Name',
      storeAddress: 'Store Address',
      town: 'Town',
      postalCode: 'Postal Code',
      province: 'Province',
      lat: 'lat',
      lng: 'lng',
      tradingHours: 'Trading Hours',
      telephoneNo: 'Telephone No.',
      dispensaryNo: 'Dispensary No.',
      email: 'email',
    },
    required: ['siteNo', 'practiceNo'],
    toUnified: dischemToUnified,
  };
  readonly capabilities = { canCancel: true, showFinancials: false, showLineItems: false };

  constructor(
    private providerService: ProviderService,
    private dischemApi: DischemApiService,
    private streamSource: FirestoreStreamStatusSource
  ) {}

  allowed$(): Observable<boolean> {
    return this.providerService.IsAllowedDischemScripts$;
  }

  readScriptId(prescription: MedicationPrescription): string | null {
    return this.dischemApi.getDischemOrderDocumentId(prescription);
  }

  async send(ctx: EscriptSendContext): Promise<EscriptSendResult> {
    const order = await this.dischemApi.sendScriptToDischem(ctx.tenantId, ctx.prescription, ctx.pharmacy.raw, ctx.pdfUrl, ctx.encounterId);
    return { identifier: (order as any).orderNumber ?? this.dischemApi.getOrderNumber(ctx.prescription), raw: order };
  }

  buildSentPayload(result: EscriptSendResult, pharmacy: UnifiedPharmacy): any {
    return { ...(result.raw || {}), original_script_image: null, Pharmacy: pharmacy.raw, Status: 'Sent' };
  }

  async cancel(ctx: EscriptCancelContext): Promise<any> {
    return this.dischemApi.cancelOrder(ctx.practiceId, ctx.patientId, ctx.prescription);
  }

  status$(practiceId: string, identifier: string): Observable<EscriptStatusRecord | null> {
    return this.streamSource.stream$('DischemOrders', identifier, doc => this.normalize(doc));
  }

  /** Mirrors DischemApiService.getOrderStatusStreamByOrderNumber: pre-append status 1, infer cancelled 3. */
  private normalize(doc: any): EscriptStatusRecord | null {
    if (!doc) {
      return null;
    }
    const sentAt = doc.createdAt ?? new Date().toISOString();
    let history: Array<{ statusCode: number; timestamp: string }> = doc.statusHistory ?? [];
    if (!history.some(h => h.statusCode === 1)) {
      history = [{ statusCode: 1, timestamp: sentAt }, ...history];
    }
    if (doc.cancelledAt && !history.some(h => h.statusCode === 3)) {
      history = [...history, { statusCode: 3, timestamp: doc.cancelledAt }];
    }
    const statusCode = doc.cancelledAt ? 3 : (doc.statusCode ?? 1);
    const statusUpdatedAt = doc.cancelledAt ? doc.cancelledAt : (doc.statusUpdatedAt ?? sentAt);
    return { ...doc, statusCode, statusUpdatedAt, statusHistory: history };
  }

  toStatusView(record: EscriptStatusRecord | null): UnifiedStatusView {
    const code = record?.statusCode ?? DischemOrderStatusCode.OrderReceived;
    const pillColor: 'orange' | 'green' | 'red' =
      code === DischemOrderStatusCode.OrderCancelled ? 'red' : DISCHEM_DELIVERED_CODES.includes(code) ? 'green' : 'orange';
    return { statusCode: code, statusLabel: DISCHEM_STATUS_LABELS[code] ?? `Status ${code}`, pillColor };
  }

  canCancelAt(statusCode: number): boolean {
    return DISCHEM_CANCELLABLE_CODES.includes(statusCode);
  }

  buildTimelineSteps(record: EscriptStatusRecord): EscriptTimelineStep[] {
    return buildDischemTimelineSteps(record as DischemOrderStatusRecord) as EscriptTimelineStep[];
  }
}
